// components/PowerBI/SubscriptionScheduler.jsx
import React, { useState, useRef, useEffect } from 'react';
import './SubscriptionScheduler.css';

const REPEAT_OPTIONS = [
  { value: 'after_refresh', label: 'Após atualização de dados (uma vez por dia)' },
  { value: 'hourly',        label: 'A cada hora' },
  { value: 'daily',         label: 'Diário' },
  { value: 'weekly',        label: 'Semanal' },
  { value: 'monthly',       label: 'Mensal' },
];

const WEEKDAYS = [
  { value: 'MON', label: 'Segunda' },
  { value: 'TUE', label: 'Terça'   },
  { value: 'WED', label: 'Quarta'  },
  { value: 'THU', label: 'Quinta'  },
  { value: 'FRI', label: 'Sexta'   },
  { value: 'SAT', label: 'Sábado'  },
  { value: 'SUN', label: 'Domingo' },
];

const TIMEZONES = [
  { value: 'Europe/Lisbon',    label: '(UTC+0/+1) Lisboa / Dublin / Londres' },
  { value: 'Europe/London',    label: '(UTC+0/+1) Londres' },
  { value: 'Europe/Paris',     label: '(UTC+1/+2) Paris / Berlim / Amsterdão' },
  { value: 'Europe/Athens',    label: '(UTC+2/+3) Atenas / Bucareste' },
  { value: 'America/Sao_Paulo',label: '(UTC-3) Brasília / São Paulo' },
  { value: 'America/Bogota',   label: '(UTC-5) Bogotá / Lima' },
  { value: 'UTC',              label: '(UTC) Coordenado Universal' },
];

const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00','05','10','15','20','25','30','35','40','45','50','55'];

// ── Custom Select ─────────────────────────────────────────────────────────────
function CustomSelect({ value, onChange, options, className = '', disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const selected = options.find(o => o.value === value) || options[0];

  return (
    <div ref={ref} className={`ss-select ${className} ${open ? 'ss-select--open' : ''} ${disabled ? 'ss-select--disabled' : ''}`}>
      <button
        type="button"
        className="ss-select__trigger"
        onClick={() => !disabled && setOpen(p => !p)}
      >
        <span className="ss-select__label">{selected?.label}</span>
        <svg className="ss-select__arrow" viewBox="0 0 20 20" fill="none">
          <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <ul className="ss-select__menu" role="listbox">
          {options.map(opt => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`ss-select__item ${opt.value === value ? 'ss-select__item--active' : ''}`}
              onMouseDown={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── SubscriptionScheduler ─────────────────────────────────────────────────────
/**
 * Props:
 *   value    – initial state object (optional)
 *   onChange – called with full state whenever anything changes
 */
export default function SubscriptionScheduler({ value = {}, onChange }) {
  const [repeat,       setRepeat]       = useState(value.repeat       || 'daily');
  const [monthDayType, setMonthDayType] = useState(value.monthDayType || 'specific');
  const [monthDay,     setMonthDay]     = useState(value.monthDay     || 1);
  const [weekdays,     setWeekdays]     = useState(value.weekdays     || ['MON']);
  const [hour,         setHour]         = useState(value.hour         || '08');
  const [minute,       setMinute]       = useState(value.minute       || '00');
  const [timezone,     setTimezone]     = useState(value.timezone     || 'Europe/Lisbon');

  // Keep local state in sync if parent resets value (e.g. editing existing sub)
  useEffect(() => {
    if (value.repeat)       setRepeat(value.repeat);
    if (value.monthDayType) setMonthDayType(value.monthDayType);
    if (value.monthDay)     setMonthDay(value.monthDay);
    if (value.weekdays)     setWeekdays(value.weekdays);
    if (value.hour)         setHour(value.hour);
    if (value.minute)       setMinute(value.minute);
    if (value.timezone)     setTimezone(value.timezone);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.repeat, value.timezone]); // only re-sync on meaningful parent resets

  const emit = (patch) => {
    onChange?.({
      repeat, monthDayType, monthDay, weekdays, hour, minute, timezone,
      ...patch,
    });
  };

  const set = (field, val, setter) => { setter(val); emit({ [field]: val }); };

  const toggleWeekday = (day) => {
    const next = weekdays.includes(day)
      ? weekdays.filter(d => d !== day)
      : [...weekdays, day];
    if (next.length === 0) return; // must keep at least one
    setWeekdays(next);
    emit({ weekdays: next });
  };

  const clampDay = (v) => Math.max(1, Math.min(31, Number(v) || 1));

  const showTime     = repeat !== 'after_refresh' && repeat !== 'hourly';
  const showMinOnly  = repeat === 'hourly'; // for hourly we only need minutes offset

  return (
    <div className="ss-root">

      {/* ── Repeat ── */}
      <div className="ss-field">
        <label className="ss-label">
          Repetir <span className="ss-required">*</span>
        </label>
        <CustomSelect
          value={repeat}
          onChange={v => set('repeat', v, setRepeat)}
          options={REPEAT_OPTIONS}
        />
      </div>

      {/* ── Weekly: day pills ── */}
      {repeat === 'weekly' && (
        <div className="ss-field">
          <label className="ss-label">
            Em que dias <span className="ss-required">*</span>
          </label>
          <div className="ss-weekdays">
            {WEEKDAYS.map(d => (
              <button
                key={d.value}
                type="button"
                className={`ss-day-btn ${weekdays.includes(d.value) ? 'ss-day-btn--active' : ''}`}
                onClick={() => toggleWeekday(d.value)}
              >
                {d.label.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Monthly: day picker ── */}
      {repeat === 'monthly' && (
        <div className="ss-field">
          <label className="ss-label">
            Todos os meses no(s) dia(s) <span className="ss-required">*</span>
          </label>
          <div className="ss-radio-group">
            <label className="ss-radio-row">
              <input
                type="radio"
                name="ss-monthDayType"
                checked={monthDayType === 'specific'}
                onChange={() => set('monthDayType', 'specific', setMonthDayType)}
              />
              <input
                type="number"
                className="ss-day-input"
                min={1} max={31}
                value={monthDay}
                disabled={monthDayType !== 'specific'}
                onChange={e => set('monthDay', clampDay(e.target.value), setMonthDay)}
              />
            </label>
            <label className="ss-radio-row">
              <input
                type="radio"
                name="ss-monthDayType"
                checked={monthDayType === 'last'}
                onChange={() => set('monthDayType', 'last', setMonthDayType)}
              />
              <span className="ss-radio-label">Último dia do mês</span>
            </label>
          </div>
        </div>
      )}

      {/* ── Hourly: minute offset only ── */}
      {showMinOnly && (
        <div className="ss-field">
          <label className="ss-label">
            Minuto de execução <span className="ss-required">*</span>
          </label>
          <div className="ss-time-row">
            <CustomSelect
              className="ss-select--sm"
              value={minute}
              onChange={v => set('minute', v, setMinute)}
              options={MINUTES.map(m => ({ value: m, label: `:${m}` }))}
            />
            <span className="ss-time-hint">de cada hora</span>
          </div>
        </div>
      )}

      {/* ── Daily / Weekly / Monthly: full time ── */}
      {showTime && (
        <div className="ss-field">
          <label className="ss-label">
            Hora agendada <span className="ss-required">*</span>
          </label>
          <div className="ss-time-row">
            <CustomSelect
              className="ss-select--sm"
              value={hour}
              onChange={v => set('hour', v, setHour)}
              options={HOURS.map(h => ({ value: h, label: h }))}
            />
            <span className="ss-time-sep">:</span>
            <CustomSelect
              className="ss-select--sm"
              value={minute}
              onChange={v => set('minute', v, setMinute)}
              options={MINUTES.map(m => ({ value: m, label: m }))}
            />
          </div>
        </div>
      )}

      {/* ── Timezone ── */}
      <div className="ss-field">
        <label className="ss-label">
          Fuso horário <span className="ss-required">*</span>
        </label>
        <CustomSelect
          value={timezone}
          onChange={v => set('timezone', v, setTimezone)}
          options={TIMEZONES}
        />
      </div>

    </div>
  );
}